import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  Check,
  Megaphone,
  Send,
  TestTube2,
  Users,
} from 'lucide-react';
import { ART_BESCHREIBUNGEN, type RundnachrichtArt } from '@genealogy-app/shared';
import {
  fetchRundnachrichten,
  fetchVorschau,
  sendeProbe,
  sendeRundnachricht,
} from '@/api/rundnachrichten.api';
import { fetchTarife } from '@/api/plans.api';
import { getSmtpConfig } from '@/api/smtp.api';
import { useConfirm } from '@/components/common/ConfirmDialog';

// ─── Rundnachrichten ─────────────────────────────────────────────────────────
//
// Die weitreichendste Einzelhandlung, die diese Konsole kennt: ein Klick, und
// jeder Kunde hat eine E-Mail. Zurückholen lässt sich davon nichts.
//
// Die Seite ist deshalb bewusst umständlicher als nötig. Wer versenden will,
// kommt an drei Dingen nicht vorbei: er sieht die Zahl der Empfänger und eine
// Stichprobe ihrer Adressen, er kann sich vorher eine Probe an sich selbst
// schicken, und er muss den Versand in einem Dialog bestätigen, in dem die
// Zahl noch einmal steht.

function zeit(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function RundnachrichtenPage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [art, setArt] = useState<RundnachrichtArt>('betrieb');
  const [planId, setPlanId] = useState<string>('');
  const [nurBesitzer, setNurBesitzer] = useState(false);
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');

  const kreis = useMemo(
    () => ({ art, planId: planId || null, nurBesitzer }),
    [art, planId, nurBesitzer],
  );

  const { data: smtp } = useQuery({ queryKey: ['smtp'], queryFn: getSmtpConfig });
  const { data: tarife } = useQuery({ queryKey: ['admin', 'plans'], queryFn: fetchTarife });

  const verlauf = useQuery({
    queryKey: ['admin', 'rundnachrichten'],
    queryFn: fetchRundnachrichten,
    // Solange eine Nachricht läuft, den Fortschritt nachführen.
    refetchInterval: (q) =>
      q.state.data?.nachrichten.some((n) => n.status === 'laeuft') ? 2000 : false,
  });

  const vorschau = useQuery({
    queryKey: ['admin', 'rundnachrichten', 'vorschau', kreis],
    queryFn: () => fetchVorschau(kreis),
  });

  // Wechselt die Art, ändert sich der Kreis – die alte Zahl wäre irreführend.
  useEffect(() => {
    void queryClient.invalidateQueries({
      queryKey: ['admin', 'rundnachrichten', 'vorschau'],
    });
  }, [art, planId, nurBesitzer, queryClient]);

  // Leere Felder gehen als null hinaus, nicht als leerer String: „kein Link"
  // und „ein Link, der nichts enthält" sollen im Nachweis nicht gleich
  // aussehen.
  const linkfelder = {
    linkUrl: linkUrl.trim() || null,
    linkText: linkText.trim() || null,
  };

  const probe = useMutation({
    mutationFn: () => sendeProbe({ art, betreff, text, ...linkfelder }),
    onSuccess: (r) => toast.success(`Probe an ${r.an} verschickt.`),
    onError: (e: Error) => toast.error(e.message),
  });

  const versand = useMutation({
    mutationFn: (anzahl: number) =>
      sendeRundnachricht({ ...kreis, betreff, text, ...linkfelder, bestaetigteAnzahl: anzahl }),
    onSuccess: (n) => {
      toast.success(`Versand an ${n.empfaenger} Empfänger gestartet.`);
      setBetreff('');
      setText('');
      setLinkUrl('');
      setLinkText('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'rundnachrichten'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const beschreibung = ART_BESCHREIBUNGEN.find((b) => b.art === art)!;
  const anzahl = vorschau.data?.anzahl ?? 0;
  const bereit = betreff.trim().length > 0 && text.trim().length > 0;
  const laeuft = verlauf.data?.nachrichten.some((n) => n.status === 'laeuft') ?? false;

  const absenden = async () => {
    if (!vorschau.data) return;
    const ok = await confirm({
      title: `An ${anzahl} Empfänger senden?`,
      message: (
        <>
          <p>
            <strong>{beschreibung.label}:</strong> „{betreff}"
          </p>
          <p className="mt-2">
            Die Nachricht geht an <strong>{anzahl}</strong>{' '}
            {anzahl === 1 ? 'Konto' : 'Konten'}
            {planId && tarife
              ? ` im Tarif „${tarife.data.find((t) => t.id === planId)?.name ?? '?'}"`
              : ''}
            {nurBesitzer ? ', die einen Stammbaum besitzen' : ''}.
          </p>
          <p className="mt-2 text-slate-500">
            E-Mails lassen sich nicht zurückholen.
          </p>
        </>
      ),
      confirmLabel: 'Jetzt senden',
      variant: 'danger',
    });
    if (ok) versand.mutate(anzahl);
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900 sm:text-2xl">
          <Megaphone className="h-5 w-5 text-slate-400" aria-hidden="true" />
          Rundnachrichten
        </h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Eine Nachricht an Ihre Kunden – angekündigte Wartung, eine Störung,
          ein Hinweis. Verschickt wird per E-Mail.
        </p>
      </header>

      {!smtp && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Es ist kein SMTP-Server eingerichtet. Ohne ihn lässt sich nichts
            verschicken – das steht unter <strong>E-Mail</strong>.
          </span>
        </p>
      )}

      {/* ── Entwurf ────────────────────────────────────────────────────── */}
      <section className="space-y-5 rounded-xl border border-slate-200 bg-white p-5">
        <div>
          <span className="text-xs font-medium text-slate-700">Art</span>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {ART_BESCHREIBUNGEN.map((b) => (
              <button
                key={b.art}
                onClick={() => setArt(b.art)}
                className={`rounded-lg border p-3 text-left transition ${
                  art === b.art
                    ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-100'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <span className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
                  {art === b.art && <Check className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" />}
                  {b.label}
                </span>
                <span className="mt-1 block text-xs text-slate-500">{b.bedeutung}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="betreff" className="text-xs font-medium text-slate-700">
            Betreff
          </label>
          <input
            id="betreff"
            value={betreff}
            maxLength={300}
            onChange={(e) => setBetreff(e.target.value)}
            placeholder="Wartungsarbeiten am Sonntag, 10. August"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <div>
          <label htmlFor="text" className="text-xs font-medium text-slate-700">
            Nachricht
          </label>
          <textarea
            id="text"
            rows={9}
            value={text}
            maxLength={20000}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              'Guten Tag,\n\nam Sonntag, den 10. August, steht die Anwendung von 2 bis 4 Uhr still. In dieser Zeit ist keine Anmeldung möglich.\n\nMit freundlichen Grüßen'
            }
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          <p className="mt-1 text-xs text-slate-400">
            Reiner Text. Eine Leerzeile trennt zwei Absätze. Formatierungen
            gibt es bewusst nicht – so kann nichts im Postfach Ihrer Kunden
            anders aussehen als hier.
          </p>
        </div>

        {/* Der Link steht in eigenen Feldern und nicht im Fließtext: der Text
            bleibt damit reiner Text, und was verlinkt wird, steht an einer
            Stelle, die sich prüfen lässt. */}
        <div className="grid gap-3 sm:grid-cols-[1fr,12rem]">
          <div>
            <label htmlFor="linkUrl" className="block text-xs font-medium text-slate-700">
              Link <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              id="linkUrl"
              type="url"
              value={linkUrl}
              maxLength={2000}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://…"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div>
            <label htmlFor="linkText" className="block text-xs font-medium text-slate-700">
              Beschriftung
            </label>
            <input
              id="linkText"
              type="text"
              value={linkText}
              maxLength={120}
              disabled={!linkUrl.trim()}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="Jetzt ansehen"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>
          <p className="text-xs text-slate-400 sm:col-span-2">
            Erscheint als Schaltfläche unter dem Text, mit der Adresse darunter
            zum Abtippen – manche Postfächer zeigen Schaltflächen nicht an. Nur
            vollständige Adressen mit <code>http://</code> oder{' '}
            <code>https://</code>.
          </p>
        </div>
      </section>

      {/* ── Empfängerkreis ─────────────────────────────────────────────── */}
      <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Users className="h-4 w-4 text-slate-400" aria-hidden="true" />
          Empfängerkreis
        </h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="tarif" className="text-xs font-medium text-slate-700">
              Tarif
            </label>
            <select
              id="tarif"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="">Alle Tarife</option>
              {tarife?.data.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.isDefault ? ' (Vorgabe)' : ''}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-start gap-2 pt-6 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={nurBesitzer}
              onChange={(e) => setNurBesitzer(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300"
            />
            <span>
              Nur Stammbaum-Besitzer
              <span className="block text-xs text-slate-400">
                Für Themen, die nur den betreffen, dem der Baum gehört –
                Speicher, Abrechnung, Kontingente.
              </span>
            </span>
          </label>
        </div>

        <div className="rounded-lg bg-slate-50 p-3 text-sm">
          {vorschau.isLoading ? (
            <span className="text-slate-500">Empfänger werden ermittelt…</span>
          ) : vorschau.isError ? (
            <span className="text-red-700">Empfänger konnten nicht ermittelt werden.</span>
          ) : (
            <>
              <p className="font-medium text-slate-900">
                {anzahl} {anzahl === 1 ? 'Empfänger' : 'Empfänger'}
                {vorschau.data && vorschau.data.abgemeldet > 0 && (
                  <span className="font-normal text-slate-500">
                    {' '}
                    · {vorschau.data.abgemeldet} abgemeldet und deshalb nicht dabei
                  </span>
                )}
              </p>
              {vorschau.data && vorschau.data.beispiele.length > 0 && (
                <p className="mt-1 text-xs text-slate-500">
                  z.&nbsp;B. {vorschau.data.beispiele.join(', ')}
                  {anzahl > vorschau.data.beispiele.length ? ' …' : ''}
                </p>
              )}
              {anzahl === 0 && (
                <p className="mt-1 text-xs text-amber-700">
                  Niemand passt auf diesen Kreis. So lässt sich nichts versenden.
                </p>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── Absenden ───────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button
          onClick={() => probe.mutate()}
          disabled={!bereit || !smtp || probe.isPending}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <TestTube2 className="h-4 w-4" aria-hidden="true" />
          {probe.isPending ? 'Wird verschickt…' : 'Probe an mich'}
        </button>
        <button
          onClick={absenden}
          disabled={!bereit || !smtp || anzahl === 0 || laeuft || versand.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <Send className="h-4 w-4" aria-hidden="true" />
          {laeuft ? 'Ein Versand läuft…' : `An ${anzahl} senden`}
        </button>
      </div>

      {/* ── Verlauf ────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-slate-200 bg-white">
        <h2 className="border-b border-slate-200 px-5 py-3 text-sm font-semibold text-slate-900">
          Bisher versandt
        </h2>
        {verlauf.data?.nachrichten.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-500">
            Es wurde noch keine Rundnachricht verschickt.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {verlauf.data?.nachrichten.map((n) => (
              <li key={n.id} className="px-5 py-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-slate-900">{n.betreff}</span>
                  <span className="text-xs text-slate-400">{zeit(n.erstelltAm)}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {ART_BESCHREIBUNGEN.find((b) => b.art === n.art)?.label ?? n.art}
                  {n.planName ? ` · Tarif ${n.planName}` : ''}
                  {n.nurBesitzer ? ' · nur Besitzer' : ''}
                  {n.erstelltVon ? ` · von ${n.erstelltVon}` : ''}
                </p>
                <p className="mt-1 text-xs">
                  {n.status === 'laeuft' && (
                    <span className="text-blue-700">
                      läuft – {n.zugestellt + n.fehler} von {n.empfaenger}
                    </span>
                  )}
                  {n.status === 'fertig' && (
                    <span className={n.fehler > 0 ? 'text-amber-700' : 'text-emerald-700'}>
                      {n.zugestellt} zugestellt
                      {n.fehler > 0 ? `, ${n.fehler} fehlgeschlagen` : ''}
                    </span>
                  )}
                  {n.status === 'abgebrochen' && (
                    <span className="text-red-700">
                      abgebrochen – {n.zugestellt} von {n.empfaenger} zugestellt
                    </span>
                  )}
                </p>
                {n.fehlerText && (
                  <pre className="mt-2 overflow-x-auto rounded bg-red-50 p-2 text-[11px] text-red-800">
                    {n.fehlerText}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default RundnachrichtenPage;
